import type { CanvasObject } from '../types';
import type { Artboard } from '../hooks/useCanvasTransform';

/**
 * 객체와 아트보드 간의 겹치는 영역 계산
 */
export function calculateOverlapArea(obj: CanvasObject, artboard: Artboard): number {
  const objLeft = obj.x;
  const objRight = obj.x + obj.width;
  const objTop = obj.y;
  const objBottom = obj.y + obj.height;
  
  const artboardLeft = artboard.x;
  const artboardRight = artboard.x + artboard.width;
  const artboardTop = artboard.y;
  const artboardBottom = artboard.y + artboard.height;
  
  // 겹치는 영역 계산
  const overlapLeft = Math.max(objLeft, artboardLeft);
  const overlapRight = Math.min(objRight, artboardRight);
  const overlapTop = Math.max(objTop, artboardTop);
  const overlapBottom = Math.min(objBottom, artboardBottom);
  
  // 겹치는 영역이 있는지 확인
  if (overlapLeft < overlapRight && overlapTop < overlapBottom) {
    const overlapWidth = overlapRight - overlapLeft;
    const overlapHeight = overlapBottom - overlapTop;
    return overlapWidth * overlapHeight;
  }
  
  return 0; // 겹치지 않음
}

/**
 * 객체와 가장 많이 겹치는 아트보드 찾기
 * @param obj 객체
 * @param artboards 아트보드 배열
 * @returns 가장 많이 겹치는 아트보드 ID (100px² 이상 겹쳐야 함), 없으면 null
 */
export function getBestArtboardForObject(obj: CanvasObject, artboards: Artboard[]): string | null {
  let bestArtboard = null;
  let maxOverlapArea = 0;
  
  artboards.forEach(artboard => {
    const overlapArea = calculateOverlapArea(obj, artboard);
    // 최소 100px² 이상 겹쳐야 종속 대상으로 간주
    if (overlapArea > maxOverlapArea && overlapArea > 100) {
      maxOverlapArea = overlapArea;
      bestArtboard = artboard.id;
    }
  });
  
  return bestArtboard;
}

/**
 * 클릭된 아트보드 찾기
 * @param x 마우스 X 좌표 (월드 좌표)
 * @param y 마우스 Y 좌표 (월드 좌표)
 * @param artboards 아트보드 배열
 * @returns 클릭된 아트보드 ID, 없으면 null
 */
export function getClickedArtboard(
  x: number,
  y: number,
  artboards: Artboard[]
): string | null {
  for (const artboard of artboards) {
    if (x >= artboard.x && x <= artboard.x + artboard.width &&
        y >= artboard.y && y <= artboard.y + artboard.height) {
      return artboard.id;
    }
  }
  return null;
}

